import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import {
    DEACTIVATION_REASONS,
    NOTE_MAX,
    OTHER_TEXT_MAX,
    todayEt,
    validateDeactivation,
    deactivationPayload,
    deactivateConfirmText,
} from '../utils/deactivationReasons';

// Deactivate a patient: a reason from the picklist is required and recorded (patient + status audit + log).
// onConfirm receives the POST /patient/block body ({ block: true, reasonCode, ... }).
const DeactivatePatientModal = ({ show, patient, onCancel, onConfirm, submitting = false }) => {
    const [reasonCode, setReasonCode] = useState('');
    const [reasonText, setReasonText] = useState('');
    const [note, setNote] = useState('');
    const [effectiveDate, setEffectiveDate] = useState(todayEt());
    const [dateOfDeath, setDateOfDeath] = useState('');
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (show) {
            setReasonCode('');
            setReasonText('');
            setNote('');
            setEffectiveDate(todayEt());
            setDateOfDeath('');
            setConfirming(false);
        }
    }, [show]);

    const form = { reasonCode, reasonText, note, effectiveDate, dateOfDeath };
    const error = validateDeactivation(form);
    const today = todayEt();

    return (
        <Modal show={show} onHide={onCancel} aria-labelledby="deactivate-patient-title">
            <Modal.Header closeButton>
                <Modal.Title id="deactivate-patient-title" as="h6">Deactivate patient</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {confirming ? (
                    <p data-testid="deactivate-confirm-text" style={{ fontSize: '14px' }}>
                        {deactivateConfirmText(patient, reasonCode, reasonText)}
                    </p>
                ) : (
                    <>
                        <small style={{ color: 'gray' }}>
                            The patient will no longer be able to log in, is marked inactive and drops off active
                            reports. The reason is recorded in the audit log.
                        </small>
                        <label htmlFor="deactivate-reason" className="mt-2 d-block" style={{ fontSize: '13px' }}>
                            Reason (required)
                        </label>
                        <select
                            id="deactivate-reason"
                            className="form-control"
                            value={reasonCode}
                            onChange={(e) => setReasonCode(e.target.value)}
                        >
                            <option value="">Select a reason…</option>
                            {DEACTIVATION_REASONS.map((r) => (
                                <option key={r.code} value={r.code}>{r.label}</option>
                            ))}
                        </select>
                        {reasonCode === 'other' && (
                            <>
                                <label htmlFor="deactivate-other" className="mt-2 d-block" style={{ fontSize: '13px' }}>
                                    Describe the reason (required)
                                </label>
                                <input
                                    id="deactivate-other"
                                    type="text"
                                    className="form-control"
                                    maxLength={OTHER_TEXT_MAX}
                                    value={reasonText}
                                    onChange={(e) => setReasonText(e.target.value)}
                                />
                            </>
                        )}
                        {reasonCode === 'deceased' && (
                            <>
                                <label htmlFor="deactivate-dod" className="mt-2 d-block" style={{ fontSize: '13px' }}>
                                    Date of death (optional)
                                </label>
                                <input
                                    id="deactivate-dod"
                                    type="date"
                                    className="form-control"
                                    max={today}
                                    value={dateOfDeath}
                                    onChange={(e) => setDateOfDeath(e.target.value)}
                                />
                            </>
                        )}
                        <label htmlFor="deactivate-effective" className="mt-2 d-block" style={{ fontSize: '13px' }}>
                            Effective date
                        </label>
                        <input
                            id="deactivate-effective"
                            type="date"
                            className="form-control"
                            max={today}
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                        />
                        <label htmlFor="deactivate-note" className="mt-2 d-block" style={{ fontSize: '13px' }}>
                            Note (optional)
                        </label>
                        <textarea
                            id="deactivate-note"
                            className="form-control"
                            rows={2}
                            maxLength={NOTE_MAX}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                        <small style={{ color: 'gray' }}>{note.length}/{NOTE_MAX}</small>
                        {reasonCode && error && <div className="text-danger" style={{ fontSize: '12px' }}>{error}</div>}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {confirming ? (
                    <>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setConfirming(false)}>Back</button>
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={!!error || submitting}
                            onClick={() => onConfirm(deactivationPayload(form))}
                        >
                            Deactivate Profile
                        </button>
                    </>
                ) : (
                    <>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={!!error}
                            onClick={() => setConfirming(true)}
                        >
                            Continue
                        </button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default DeactivatePatientModal;
